import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { AuthGuardDirective } from '@twist/core';
import { TwBadgeComponent, TwBadgeStatus } from '@twist/ui-primitives';
// F19 Wave 8 — render tab icons with the STATIC `ui-svg-icon` sprite
// (`SvgIconComponent`), the SAME proven path the sidebar nav uses. The earlier
// `tw-icon` path resolved `lucide:*` refs through the IconStore HTTP API; those
// refs are not in the prefetched groups and 404 on demand → tombstone → BLANK.
// `ui-svg-icon` renders a compile-time inline `<svg>` keyed by a `SvgIcons`
// enum value (no HTTP), so the icons always render. There is NO project-graph
// cycle: `@twist/ui/components` does not (transitively) depend on
// `ui-advanced-primitives` (verified via `nx graph`), and `ui-layout` already
// consumes `SvgIconComponent` the same way.
import { SvgIconComponent, SvgIcons } from '@twist/ui/components';
import {
  NavBadge,
  NavBadgeProvider,
  NavDescriptor,
  NavRegistry,
  NavVisibilityProvider,
  NAV_BADGE_PROVIDER,
  NAV_VISIBILITY_PROVIDER,
} from '@twist/ui-navigation';

/**
 * `tw-nav-tabs` — registry-fed, ROUTED tab navigation.
 *
 * Reads the `tabs`-surface children of the descriptor identified by
 * `navId` (`NavRegistry.childrenForSurface(navId, 'tabs')`) and renders
 * a `<nav>` of `<a routerLink>` anchors — one per child. The active tab
 * carries `aria-current="page"`.
 *
 * ### A11y choice — NOT a tablist
 *
 * This is page-level navigation between routed views, so it deliberately
 * does NOT use `role="tablist"` / `role="tab"` / `aria-selected`. Those
 * roles describe an in-page tab WIDGET (content panels toggled in place),
 * which would be a false a11y promise here: activating a tab navigates.
 * The correct pattern for routed tabs is a labelled `<nav>` of links with
 * `aria-current="page"` on the active one — see the shipped content
 * `tw-tabs` primitive for the in-page widget (which we deliberately do
 * NOT reuse for routed nav).
 *
 * Per-tab security is enforced with the shared `*authGuard` structural
 * directive on `child.security`. Count pills come from the nav-badge
 * model (static `descriptor.badge`, overridden by a `NAV_BADGE_PROVIDER`
 * for the child's id) rendered via `tw-badge`.
 *
 * Per-tab VISIBILITY is data-driven via the `NAV_VISIBILITY_PROVIDER` seam
 * (mirrors the badge seam): a tab is shown by default and is hidden only when
 * some registered provider returns `false` for its id. The filter is applied
 * consumer-side in the `tabs` computed — the `NavRegistry` stays pure — and
 * layers ON TOP of the `*authGuard` security gate (it never replaces it).
 */
@Component({
  selector: 'tw-nav-tabs',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, AuthGuardDirective, TwBadgeComponent, SvgIconComponent],
  templateUrl: './tw-nav-tabs.component.html',
  styleUrl: './tw-nav-tabs.component.scss',
  host: {
    class: 'tw-nav-tabs',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TwNavTabsComponent {
  private readonly registry = inject(NavRegistry);
  private readonly badgeProviders =
    (inject(NAV_BADGE_PROVIDER, { optional: true }) as unknown as NavBadgeProvider[] | null) ??
    [];
  private readonly visibilityProviders =
    (inject(NAV_VISIBILITY_PROVIDER, { optional: true }) as unknown as
      | NavVisibilityProvider[]
      | null) ?? [];
  private readonly _providerBadgeCache = new Map<string, () => NavBadge | null>();
  private readonly _providerVisibleCache = new Map<string, () => boolean>();

  /** The parent descriptor id whose `tabs`-surface children are the tabs. */
  readonly navId = input.required<string>();

  /** Optional aria-label for the nav landmark (defaults to the parent label). */
  readonly ariaLabel = input<string | undefined>(undefined);

  /**
   * The ordered tabs-surface children of the parent descriptor, filtered to
   * those VISIBLE per the `NAV_VISIBILITY_PROVIDER` seam (default-visible).
   * The registry call stays pure; visibility is resolved consumer-side here.
   */
  protected readonly tabs = computed<NavDescriptor[]>(() =>
    this.registry
      .childrenForSurface(this.navId(), 'tabs')
      .filter((child) => this._providerVisible(child.id)()),
  );

  /** Resolved nav landmark label: explicit input, else the parent label. */
  protected readonly navLabel = computed<string>(
    () => this.ariaLabel() ?? this.registry.descriptor(this.navId())?.label ?? 'Tabs',
  );

  /** Effective badge for a tab: NAV_BADGE_PROVIDER override, else static. */
  protected effectiveBadge(child: NavDescriptor): NavBadge | null {
    const override = this._providerBadge(child.id)();
    return override ?? child.badge ?? null;
  }

  /**
   * Coerce a descriptor's `icon` (a free-form `string`) to the `SvgIcons`
   * key the `ui-svg-icon` component expects. Mirrors the sidebar nav's
   * `asSvgIcon` cast — the descriptor author is responsible for using a real
   * `SvgIcons` value (e.g. `dashboard`, `gears`, `tag`, `timeLapse`); an
   * unknown key falls through `ui-svg-icon`'s `@default` glyph, never blank.
   */
  protected asSvgIcon(icon: string): keyof { [key in SvgIcons]: string } {
    return icon as keyof { [key in SvgIcons]: string };
  }

  protected badgeType(badge: NavBadge): 'status' | 'tag' {
    return badge.kind === 'status-count' ? 'status' : 'tag';
  }

  protected badgeStatus(badge: NavBadge): TwBadgeStatus {
    return badge.kind === 'status-count' ? badge.severity : 'neutral';
  }

  protected badgeLabel(badge: NavBadge): string {
    switch (badge.kind) {
      case 'count':
        return badge.cap !== undefined && badge.value > badge.cap
          ? `${badge.cap}+`
          : `${badge.value}`;
      case 'text':
        return badge.text;
      case 'status-count':
        return `${badge.value}`;
    }
  }

  private _providerBadge(navId: string): () => NavBadge | null {
    let resolver = this._providerBadgeCache.get(navId);
    if (!resolver) {
      const signals = this.badgeProviders.map((p) => p.badgeFor(navId));
      resolver = () => {
        for (const s of signals) {
          const value = s();
          if (value) {
            return value;
          }
        }
        return null;
      };
      this._providerBadgeCache.set(navId, resolver);
    }
    return resolver;
  }

  /**
   * Effective visibility for a tab id: a tab is visible by default and hidden
   * only when some registered `NAV_VISIBILITY_PROVIDER` returns `false`. The
   * resolver is cached per id (the signals are created once).
   */
  private _providerVisible(navId: string): () => boolean {
    let resolver = this._providerVisibleCache.get(navId);
    if (!resolver) {
      const signals = this.visibilityProviders.map((p) => p.visibleFor(navId));
      resolver = () => signals.every((s) => s());
      this._providerVisibleCache.set(navId, resolver);
    }
    return resolver;
  }
}
